import { ConductorAllChildrenActive, Router } from "@frui.ts/screens";
import { computed } from "mobx";
import { Modules } from "config";
import SecurityService from "services/securityService";
import LocalizationService from "services/localizationService";
import RoleUsersListViewModel from "./roleUsersListViewModel";
import RoleUserCreateViewModel from "./roleUserCreateViewModel";
import RoleUpdateViewModel from "./roleUpdateViewModel";
import type { interfaces } from "inversify";
import AppRouter from "services/router";
import NotificationService from "services/notificationService";
import EventBus from "services/eventBus";
import RoleViewModel from "../rolesViewModel";
import { bound } from "@frui.ts/helpers";

@Router.registerRoute({ route: ":roleId", name: Router.Self })
export default class RoleDetailViewModel extends ConductorAllChildrenActive<
  RoleUpdateViewModel | RoleUsersListViewModel | RoleUserCreateViewModel
> {
  roleUpdateViewModel: RoleUpdateViewModel;
  roleUsersListViewModel: RoleUsersListViewModel;
  roleUserCreateViewModel: RoleUserCreateViewModel;

  constructor(
    protected itemId: string,
    public roleUpdateViewModelFactory: ReturnType<typeof RoleUpdateViewModel.Factory>,
    public roleUsersListViewModelFactory: ReturnType<typeof RoleUsersListViewModel.Factory>,
    public roleUserCreateViewModelFactory: ReturnType<typeof RoleUserCreateViewModel.Factory>,
    protected localizationService: LocalizationService,
    protected appRouter: AppRouter,
    protected notificationService: NotificationService,
    protected securityService: SecurityService,
    protected eventBus: EventBus
  ) {
    super();
    this.navigationName = `${this.itemId}`;

    this.roleUpdateViewModel = this.roleUpdateViewModelFactory(itemId);
    this.roleUsersListViewModel = this.roleUsersListViewModelFactory(itemId);
    this.roleUserCreateViewModel = this.roleUserCreateViewModelFactory(this.itemId);

    this.children.push(this.roleUpdateViewModel);
    this.children.push(this.roleUsersListViewModel);
    this.children.push(this.roleUserCreateViewModel);
  }

  @bound
  onAddUserClick() {
    this.roleUserCreateViewModel.showModal = true;
    this.tryActivateChild(this.roleUserCreateViewModel);
  }

  @computed
  get allowWrite() {
    return this.securityService.allowTo("callcenter", "write");
  }

  @computed
  get name() {
    return this.translate("edit");
  }

  @bound
  close() {
    this.requestClose();
  }

  get backLink() {
    return this.appRouter.getUrl(RoleViewModel);
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.users, `role.${code}`, size);
  }

  static Factory({ container }: interfaces.Context) {
    return (itemId: string) => {
      return new RoleDetailViewModel(
        itemId,
        container.get(RoleUpdateViewModel.Factory),
        container.get(RoleUsersListViewModel.Factory),
        container.get(RoleUserCreateViewModel.Factory),
        container.get(LocalizationService),
        container.get(AppRouter),
        container.get(NotificationService),
        container.get(SecurityService),
        container.get(EventBus)
      );
    };
  }
}
